// @ts-nocheck
import { Trans, useLingui } from '@lingui/react'
import { type CartV2 } from '@redteclab/api/clients/bully'
import {
  Dispatch,
  FunctionComponent,
  type ReactElement,
  useEffect,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { compose } from 'redux'
import './Cart.scss'

import { CHECKOUT_ADDRESS_TARGET_PAGE_CODE } from 'shared/consts'
import { DOCUMENT_TYPE, SA_SELLER_ID } from 'shared/consts/checkout'
import {
  TENANT_AT,
  TENANT_BE,
  TENANT_CH,
  TENANT_COM,
  TENANT_FR,
  TENANT_IT,
} from 'shared/consts/tenants'
import CartStickyCta from 'shared/experiments/components/CartStickyCta'
import ShippingDelayNotification from 'shared/experiments/components/ShippingDelayNotification'
import {
  CART_STICKY_CTA_EXPERIMENT,
  EXPERIMENT_VARIATION,
  TOTAAL_KORTING_AB_TEST,
} from 'shared/experiments/consts'
import { useGetExperiment } from 'shared/experiments/utils/experiments'
import { useGetExperimentPerTenant } from 'shared/experiments/utils/tenantBasedExperimentsUtils'
import { fetchCartAction } from 'shared/store/ducks/cart'
import {
  CartControllerCartSellerDiscountV2,
  CartControllerSellerGroupV2,
} from 'types/api/cart'
import { type IState } from 'types/redux/store'
import initSelect from 'views/assets/scripts/select'
import Notification, {
  NOTIFICATION_TYPES,
} from 'views/components/molecules/Notification'
import { useMaxDosageValues } from 'views/components/organisms/Cart/hooks/useMaxDosageValues'
import NowCollapsible from 'views/components/organisms/Cart/NowCollapsible'
import {
  hasSAEGroupSelector,
  redeemedRedPointsSelector,
} from 'views/components/organisms/Cart/selectors'
import CartSumBox from 'views/components/organisms/CartSumBox'
import { APPLIED_PROMOTION_INFORMATION_MODAL } from 'views/components/organisms/CartSumBox/AppliedPromotionInformationModal/names'
import RedPointsAndRXInfoBox from 'views/components/organisms/Checkout/RedPointsAndRXInfoBox'
import { RED_POINTS_INFORMATION_MODAL } from 'views/components/organisms/Checkout/RedPointsInformationModal/names'
import {
  POSITION_LAST,
  POSITION_MIDDLE,
} from 'views/components/organisms/Collapsible/consts'
import { VOUCHER_TYPE } from 'views/consts'
import { useLinePercentPromotions } from 'views/enhancers/useLinePercentPromotions'
import withMountPoints from 'views/enhancers/withMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import { isRedPointsActive } from 'views/utils/featureToggleUtils'

import CartHeader from './CartHeader'
import CartModals from './CartModals'
import { DeleteEPrescriptionEntryContext } from './DeleteEPrescriptionEntryContext'
import EmptyCartHeader from './EmptyCartHeader'
import { ErxBonusNotUsedInfoNonDiscountable } from './ErxBonusNotUsedInfoNonDiscountable'
import generateCartSubtitleFromVariantAttributes from './helper/cartSubtitleGenerator'
import { NotificationCart } from './NotificationCart'
import ProductListCart from './ProductListCart'
import RedPointsCollapsible from './RedPointsCollapsible'
import VoucherCollapsible from './VoucherCollapsible'

type CartProps = {
  mountPoints: unknown // TODO add types for mountPoints
  publicRuntimeConfig: {
    publicConfig: unknown
    pageProperties: unknown
  }
}

const getShowPrescriptionBonusNotifications = (cart: CartV2) => {
  let showERxBonusNotUsedInfo = false
  let showERxBonusNotUsedInfoNonDiscountable = false

  const sellerGroupSAE = cart.sellerGroups.find(
    it => it.sellerId === SA_SELLER_ID
  )

  const doesNotUseAnyRxBonus =
    cart.rxBonusTotal > 0 && cart.rxBonusDiscount === 0

  //prevents display of notification if prescriptionBonus for public prescriptions is turned off.
  //In that case the notification should not display with the following items in the cart:
  //1. Free Prescription
  //2.Private Prescription
  const hasOnlyPrivateBonus = cart.rxBonusTotal === cart.rxBonusToAccount

  if (!sellerGroupSAE || !doesNotUseAnyRxBonus || hasOnlyPrivateBonus) {
    return {
      showBlueNotification: false,
      showOrangeNotification: false,
    }
  }

  //checks if the Cart has at least one OTC product
  const hasOtcProducts = sellerGroupSAE?.groupEntries.some(groupEntry =>
    groupEntry.type === 'noPrescription' ? groupEntry.items.length > 0 : false
  )

  const saeRxFreePrescriptionGroup = sellerGroupSAE.groupEntries.find(
    group => group.type === 'freePrescription'
  )

  //checks if an freePrescription
  const hasFreePrescription =
    Boolean(saeRxFreePrescriptionGroup) ||
    Boolean(
      sellerGroupSAE.groupEntriesERx.find(entry =>
        entry.type === 'insurancePrescription'
          ? entry.items.some(item => item.copaymentFree)
          : false
      )
    )

  //checks if cart contains marketplace product
  const hasMP =
    (cart.sellerGroups.length === 1 && !sellerGroupSAE) ||
    cart.sellerGroups.length > 1

  if (hasFreePrescription) {
    if (!hasOtcProducts && !hasMP) {
      showERxBonusNotUsedInfo = true
    } else {
      showERxBonusNotUsedInfoNonDiscountable = true
    }
  }
  return {
    showERxBonusNotUsedInfo,
    showERxBonusNotUsedInfoNonDiscountable,
  }
}

const Cart: FunctionComponent<CartProps> = ({
  mountPoints,
  publicRuntimeConfig: {
    publicConfig: { onlineDoctorConfig, tenant },
    pageProperties: { featureToggles },
  },
}): ReactElement | null => {
  const { i18n } = useLingui()
  const dispatch: Dispatch<unknown> = useDispatch()

  const cart: CartV2 = useSelector((state: IState) => state.cart.data)

  const [hasNowProducts, setHasNowProducts] = useState(false)

  const [isRedpointsInTheMiddleWithNow, setIsRedpointsInTheMiddleWithNow] =
    useState(false)

  useEffect(() => {
    initSelect()
  })

  const hasSAEGroup = useSelector(hasSAEGroupSelector)

  const redPointsIsActive = isRedPointsActive(featureToggles)

  /**
   * Get UserState Guest/Loggedin
   */
  const loggedInUser = useSelector(
    (state: IState) => state.userSession.newLogin
  )
  const [ePrescriptionHashedToken, setEPrescriptionHashedToken] = useState()
  const redPointsPreviousAmount = useSelector(redeemedRedPointsSelector)
  const proceedCheckoutBtnLink: string = loggedInUser
    ? mountPoints.getNewCheckoutAddress().replace(/\/$/, '')
    : mountPoints.getLogin({
        targetPageCode: CHECKOUT_ADDRESS_TARGET_PAGE_CODE,
      })

  const { hasOverdrafts, maxDosageDetails } = useMaxDosageValues()

  let hasODS = false
  let providerData = null

  const [cartFetchLoading, setCartFetchLoading] = useState<string | undefined>()

  const { linePercentPromotions } = useLinePercentPromotions()
  const linePercentExperiment = useGetExperiment(TOTAAL_KORTING_AB_TEST)
  const stickyCtaExperiment = useGetExperimentPerTenant(
    CART_STICKY_CTA_EXPERIMENT
  )

  let productAmount = 0
  if (!cart.sellerGroups) {
    return null
  }

  // Count items in Cart
  cart.sellerGroups.forEach((seller: CartControllerSellerGroupV2) => {
    if (seller) {
      seller.groupEntries.forEach(entry => {
        if (entry.type === DOCUMENT_TYPE.ODS_SECTION) {
          hasODS = true
        }
        entry.items.forEach(product => {
          if (hasODS && product.odsTransactionData) {
            providerData = onlineDoctorConfig.odsProvider.find(
              x => x.providerName === product.odsTransactionData.provider
            )
          }
          productAmount += product.quantity
        })
      })
    }
  })

  cart.sellerGroups.forEach((seller: CartControllerSellerGroupV2) => {
    if (seller) {
      seller.groupEntriesERx.forEach(entry => {
        entry.items.forEach(product => {
          productAmount += product.quantity
        })
      })
    }
  })

  const vouchers: CartControllerCartSellerDiscountV2[] = []
  const nonDiscountableVouchers: string[] = []

  cart.sellerGroups.forEach((seller: CartControllerSellerGroupV2) => {
    vouchers.push(
      ...seller.discounts.filter(discount => {
        const hasDuplicateVoucher = !!vouchers.find(
          voucher => voucher.code === discount.code
        )
        return discount.type === VOUCHER_TYPE.VOUCHER && !hasDuplicateVoucher
      })
    )
    nonDiscountableVouchers.push(
      ...seller.nonDiscountVoucherCodeList.filter(actVoucher => {
        const hasDuplicateVoucher = !!nonDiscountableVouchers.find(
          existingVoucher => existingVoucher === actVoucher
        )
        return !hasDuplicateVoucher
      })
    )
  })

  cart.sellerGroups.forEach(seller => {
    seller.groupEntries.forEach(entry => {
      entry.items.forEach(item => {
        item.subtitle = generateCartSubtitleFromVariantAttributes(
          i18n,
          item.pharmaForm,
          item.variantAttributes
        )
      })
    })
    seller.groupEntriesERx.forEach(entry => {
      entry.items.forEach(item => {
        item.subtitle = generateCartSubtitleFromVariantAttributes(
          i18n,
          item.pharmaForm,
          item.variantAttributes
        )
      })
    })
  })

  // Checks for rx products in the cart
  const hasRx = cart.sellerGroups.some((group: CartControllerSellerGroupV2) =>
    group.groupEntries.some(({ type }) =>
      [
        'privatePrescription',
        'freePrescription',
        'insurancePrescription',
        'odsSection',
      ].includes(type)
    )
  )

  // Checks for eRx products in the cart
  const hasERx = cart.sellerGroups.some(
    (group: CartControllerSellerGroupV2) => group.groupEntriesERx.length > 0
  )

  const cartSumBoxDetails: {
    id: string
    label: string
    value: number
    modal: string
  }[] = [
    {
      id: 'shippingCosts',
      label: i18n._('basket.shippingCosts'),
      value: cart.shippingCostTotal,
      modal: `sellerShippingAddressBoxModal`,
    },
  ]
  if (redPointsIsActive) {
    cartSumBoxDetails.push({
      id: 'RedPoints',
      label: redPointsPreviousAmount ? i18n._('general.label.redPoints') : '',
      value: -redPointsPreviousAmount,
      modal: RED_POINTS_INFORMATION_MODAL,
    })
  }

  const hasLinePercentPromotion =
    cart.subAmountWithLineDiscounts > 0 && linePercentPromotions.length > 0
  const showLinePercentDiscountsOnLines =
    linePercentExperiment.variant === EXPERIMENT_VARIATION.DEFAULT ||
    linePercentExperiment.variant === EXPERIMENT_VARIATION.V1
  const showCouponCodeBellowSubtotal =
    linePercentExperiment.variant === EXPERIMENT_VARIATION.DEFAULT ||
    linePercentExperiment.variant === EXPERIMENT_VARIATION.V3

  if (
    hasLinePercentPromotion &&
    (!linePercentExperiment.isEnabled || showCouponCodeBellowSubtotal)
  ) {
    cartSumBoxDetails.push({
      id: 'appliedPromotions',
      // only one supported at one time
      label: linePercentPromotions[0].code,
      value: -linePercentPromotions[0].value,
      modal: APPLIED_PROMOTION_INFORMATION_MODAL,
    })
  }

  const sellerGroupSAE = cart.sellerGroups.find(
    it => it.sellerId === SA_SELLER_ID
  )
  const hasMP =
    (cart.sellerGroups.length === 1 && !sellerGroupSAE) ||
    cart.sellerGroups.length > 1
  const nonDiscountVoucherSAE = sellerGroupSAE?.nonDiscountVoucherCodeList || []

  const hasInvalidVouchers = cart.messages.find(message =>
    message.messages.find(
      invalidMessage => invalidMessage === 'voucher.invalid'
    )
  )

  /*
    The submitButtonAction function will be triggered when the customer tries to enter the checkout.
    We have to check if redeemed vouchers became invalid in the time span between redeeming the vouchers and trying to enter the checkout.
    Because a customer could have a stale cart for hours or days and in the meanwhile the vouchers could have become invalid.
  */
  const submitButtonAction = () => {
    if (
      cart.totalAppliedVouchersDiscount > 0 ||
      nonDiscountVoucherSAE.length > 0
    ) {
      dispatch(fetchCartAction(true))
      setCartFetchLoading(cart.lastUpdated)
    } else {
      window.location.href = proceedCheckoutBtnLink
    }
  }

  /*
    This following if block will check if the cart fetch is done and if the cart is updated.
    It also takes all other actions that refresh the cart on the cart page into account like
    changing the quantity of products, redeeming redpoints and so on.
   */
  if (cartFetchLoading !== undefined && cartFetchLoading !== cart.lastUpdated) {
    if (hasInvalidVouchers) {
      setCartFetchLoading(undefined)
    } else {
      window.location.href = proceedCheckoutBtnLink
    }
  }
  const hasRxExcluded = hasRx && hasMP

  {
    /* for this moment only for BE tenant hide collective order checkbox when personal max qty and maxdosage max qty are equal */
  }
  const hasStillAddableDefaultQtyAndstillAddableCollectiveOrderQtyEqual =
    maxDosageDetails?.some(
      detail =>
        detail.stillAddableDefaultQty === detail.stillAddableCollectiveOrderQty
    )
  const showMultiOrderCheckbox =
    ([TENANT_BE].includes(tenant) &&
      !hasStillAddableDefaultQtyAndstillAddableCollectiveOrderQtyEqual) ||
    ![TENANT_BE].includes(tenant)

  return (
    <DeleteEPrescriptionEntryContext.Provider
      value={[ePrescriptionHashedToken, setEPrescriptionHashedToken]}
    >
      <div>
        {productAmount > 0 && (
          <>
            <CartHeader
              productAmount={productAmount}
              hasODS={hasODS}
              providerData={providerData}
              submitButtonAction={submitButtonAction}
              disableSubmitButton={hasOverdrafts}
            />
            <div className="u-margin-large--bottom">
              <ShippingDelayNotification />
            </div>
          </>
        )}
        {productAmount === 0 && (
          <EmptyCartHeader
            loggedInUser={loggedInUser}
            continueShoppingLink={mountPoints.getHomePage()}
          />
        )}
        {hasRxExcluded && (
          <NotificationCart
            type={NOTIFICATION_TYPES.INFO}
            classNameOverride="a-full-width u-margin-x-large--bottom"
          >
            <Trans id="cart.notification.rx.excluded" />
          </NotificationCart>
        )}
        <ProductListCart
          shippingDetail={cart.shippingDetail}
          sellerGroups={cart.sellerGroups}
          currency={cart.currency}
          overdrafts={cart.overdrafts}
          showNowCases={
            [TENANT_COM, TENANT_AT].includes(tenant) && hasNowProducts
          }
        />
        {productAmount > 0 && (
          <div className="o-Cart--flex-col">
            <div className="o-Cart--row u-margin--bottom">
              {!hasSAEGroup && (
                <div className="u-margin--bottom">
                  <Notification
                    label={i18n._(
                      'cart.redPointsAndVouchers.unavailableinMpOnlyCart.infoBox'
                    )}
                    type={NOTIFICATION_TYPES.INFO}
                  />
                </div>
              )}
              {getShowPrescriptionBonusNotifications(cart)
                ?.showERxBonusNotUsedInfo ? (
                <div
                  className="u-margin--bottom u-border o-Cart-ErxNotification--Border"
                  data-qa-id="show-erx-bonus-notUsed"
                >
                  <Notification
                    className="u-background-color--background"
                    heading={
                      <Trans
                        id="cart.notification.prescriptionBonus.notUsed.headline"
                        components={{ sup: <sup /> }}
                      />
                    }
                    label={
                      <div>
                        <div className="u-margin-x-small--bottom">
                          <Trans
                            id="cart.notification.prescriptionBonus.notUsed"
                            components={{ sup: <sup /> }}
                          />
                        </div>
                        <a
                          href={mountPoints.getSearch({ query: 'redcare' })}
                          className="o-Cart-Notification-link"
                        >
                          <Trans id="cart.notification.prescriptionBonus.notUsed.discover" />
                        </a>
                      </div>
                    }
                    type="infoNoBgColor"
                  />
                </div>
              ) : null}
              {getShowPrescriptionBonusNotifications(cart)
                ?.showERxBonusNotUsedInfoNonDiscountable && (
                <ErxBonusNotUsedInfoNonDiscountable
                  searchUrl={mountPoints.getSearch({ query: 'redcare' })}
                />
              )}
              <VoucherCollapsible
                vouchers={vouchers}
                promotionInformation={cart.promotionInformation}
                nonDiscountableVouchers={nonDiscountableVouchers}
                position={redPointsIsActive ? 'first' : null}
              />

              {redPointsIsActive && (
                <RedPointsCollapsible
                  position={
                    [TENANT_COM, TENANT_AT].includes(tenant) &&
                    isRedpointsInTheMiddleWithNow
                      ? POSITION_MIDDLE
                      : POSITION_LAST
                  }
                />
              )}

              {[TENANT_COM, TENANT_AT].includes(tenant) && (
                <NowCollapsible
                  setHasNowProducts={setHasNowProducts}
                  hasNowProducts={hasNowProducts}
                  setIsRedpointsInTheMiddleWithNow={
                    setIsRedpointsInTheMiddleWithNow
                  }
                />
              )}

              {redPointsIsActive && (
                <>
                  {(cart.redPointsTotalAmount > 0 || cart.rxBonusTotal > 0) && (
                    <div className="u-margin-large--top u-margin-large--bottom l-flex l-flex--vertical-stretch">
                      <RedPointsAndRXInfoBox redpointsAndRx={cart} />
                    </div>
                  )}
                </>
              )}
            </div>
            <div
              className="o-Cart--row u-background-color--greyLight-light u-border-radius u-min-width-0"
              style={{ height: '0%' }}
            >
              <CartSumBox
                button={{
                  label: <Trans id="cart.button.label.toCheckoutShort" />,
                  id: 'ToCheckout',
                  href: proceedCheckoutBtnLink,
                }}
                className="a-box a-box--dark u-padding-large"
                currency={cart.currency}
                productAmount={
                  hasLinePercentPromotion && showLinePercentDiscountsOnLines
                    ? cart.subAmountWithLineDiscounts
                    : cart.subAmount
                }
                totalAmountDiscounts={cart.discountAmountTotal}
                showMultiOrderCheckbox={showMultiOrderCheckbox}
                showPrescriptionFollowsCheckbox={
                  !hasERx &&
                  !hasRx &&
                  hasSAEGroup &&
                  ![
                    TENANT_AT,
                    TENANT_CH,
                    TENANT_FR,
                    TENANT_IT,
                    TENANT_BE,
                  ].includes(tenant) &&
                  !hasMP
                }
                costDetails={cartSumBoxDetails}
                totalAmount={cart.totalAmount}
                vouchers={vouchers}
                rxBonusDiscount={cart.rxBonusDiscount}
                rxBonusToAccount={cart.rxBonusToAccount}
                applicableCredit={cart.applicableCredit}
                submitButtonAction={submitButtonAction}
                disableSubmitButton={hasOverdrafts}
                sellerGroups={cart.sellerGroups}
                hasErxPriceSumBox
              />
            </div>
          </div>
        )}
        <CartModals
          productAmount={productAmount}
          sellerGroups={cart.sellerGroups}
        />

        {stickyCtaExperiment.isEnabled &&
        stickyCtaExperiment.variant !== EXPERIMENT_VARIATION.DEFAULT &&
        productAmount > 0 ? (
          <CartStickyCta
            button={{
              label: <Trans id="cart.button.label.toCheckoutShort" />,
              id: 'ToCheckout',
              href: proceedCheckoutBtnLink,
            }}
            submitButtonAction={submitButtonAction}
            disableSubmitButton={hasOverdrafts}
            totalAmount={cart.totalAmount}
            currency={cart.currency}
          />
        ) : null}
      </div>
    </DeleteEPrescriptionEntryContext.Provider>
  )
}

export default compose(withShopAndRuntimeConfig, withMountPoints)(Cart)
